import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, DialogActions, DialogContent, DialogTitle, MenuItem, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import Form, {MuiRFF, Yup} from "../../form";
import useIsMounted from "../../../hooks/use-is-mounted";
import DateUtils from "../../../../core/services/date-utils";
import {FormResponseStatusList} from "../../../../packages/biz-form/core/constants/form-response-statuses";

const buildUpdatedProperties = (values, row) => {
    const updatedProperties = [];
    if (values.status !== row.status) {
        updatedProperties.push("Status");
    }
    const nextExpiry = values.statusExpiry?.isValid?.() ? DateUtils.UTCToServer(values.statusExpiry) : null;
    const prevExpiry = row.statusExpiry ?? null;
    if (nextExpiry !== prevExpiry) {
        updatedProperties.push("StatusExpiry");
    }
    const nextComment = values.reviewComment?.trim() ?? "";
    const prevComment = row.reviewComment?.trim() ?? "";
    if (nextComment !== prevComment) {
        updatedProperties.push("ReviewComment");
    }
    return updatedProperties;
};

const ReviewFormResponseDialog = ({open, onClose, response, api, onSuccess}) => {
    const {t} = useTranslation("dialogs", {keyPrefix: "reviewFormResponse"});
    const isMounted = useIsMounted();
    const [submitting, setSubmitting] = useState(false);

    const schema = useMemo(() => Yup.object().shape({
        status: Yup.string().required(),
        statusExpiry: Yup.mixed()
            .validDate()
            .nullable(),
        reviewComment: Yup.string().optional().nullable(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        status: response?.status ?? "",
        statusExpiry: response?.statusExpiry ? DateUtils.serverToUTC(response.statusExpiry) : null,
        reviewComment: response?.reviewComment ?? "",
    }), [response]);

    /**
     * Resets the submitting state when the dialog is closed to allow new submissions when reopened.
     */
    useEffect(() => {
        if (!open) {
            setSubmitting(false);
        }
    }, [open]);

    /**
     * Handles the form submission by validating changes, updating the form response via API, and managing the dialog state accordingly.
     * @type {(function(*): Promise<void>)|*}
     */
    const onSubmit = useCallback(async (values) => {
        if (!response?.id) {
            return;
        }
        const updatedProperties = buildUpdatedProperties(values, response);
        if (!updatedProperties.length) {
            onClose();
            return;
        }
        setSubmitting(true);
        const res = await api.reviewFormResponse({
            id: response.id,
            status: values.status,
            statusExpiry: values.statusExpiry?.isValid?.() ? DateUtils.UTCToServer(values.statusExpiry).format(DateUtils.withoutOffsetFormat) : null,
            reviewComment: values.reviewComment?.trim() ? values.reviewComment.trim() : null,
            updatedProperties,
        });
        if (!isMounted()) {
            return;
        }
        setSubmitting(false);
        if (res?.resultFlag) {
            onSuccess?.();
            onClose();
        }
    }, [api, isMounted, onClose, onSuccess, response]);

    const statusOptions = useMemo(() => FormResponseStatusList.map((status) => ({
        value: status,
        label: t(`statuses.${status}`),
    })), [t]);

    return (
        <Dialog open={open} setOpen={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="subtitle1" fontWeight={600} textAlign="center">
                    {t("title")}
                </Typography>
            </DialogTitle>
            <Form
                key={response?.id ?? "new"}
                initialValues={initialValues}
                validate={validate}
                onSubmit={onSubmit}
                formProps={{className: "w-100"}}
            >
                {({form}) => (
                    <>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                <Grid xs={12}>
                                    <MuiRFF.TextField
                                        name="status"
                                        label={t("status")}
                                        select
                                        fullWidth
                                        required={required.status}
                                        InputProps={{disableUnderline: true, sx: {borderRadius: 1}}}
                                    >
                                        {statusOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </MuiRFF.TextField>
                                </Grid>
                                <Grid xs={12}>
                                    <MuiRFF.DateTimePicker
                                        name={"statusExpiry"}
                                        label={t("statusExpiry")}
                                        required={required.statusExpiry}
                                        form={form}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <MuiRFF.TextField
                                        name="reviewComment"
                                        label={t("reviewComment")}
                                        multiline
                                        minRows={3}
                                        fullWidth
                                        required={required.reviewComment}
                                        InputProps={{disableUnderline: true, sx: {borderRadius: 1}}}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{m: 0, p: 0}}>
                            <Box sx={{display: "flex", gap: 1, width: "100%", justifyContent: "space-between"}}>
                                <Button
                                    variant="dialog"
                                    borderRadius={"0 8px 0 0"}
                                    onClick={onClose}
                                    disabled={submitting}
                                >
                                    {t("cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="dialog"
                                    borderRadius={"8px 0 0 0"}
                                    disabled={submitting}
                                >
                                    {submitting ? t("submitting") : t("submit")}
                                </Button>
                            </Box>
                        </DialogActions>
                    </>
                )}
            </Form>
        </Dialog>
    );
};

export default ReviewFormResponseDialog;
